import React, { useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { cn } from '@/lib/utils';
import { DraperyModule } from '../../../redux';

type HeadrailPricingTableProps = {
  rows: any[];
  shippingRow: any;
  includeShipping: boolean;
  disabled?: boolean;
  onChange(
    index: number,
    field: 'estimateDescription' | 'cost' | 'quantity' | 'markup' | 'customerPrice',
    value: string,
    isShipping?: boolean
  ): void;
  onToggleShipping(include: boolean): void;
  draperyOrderHeadrail: DraperyModule.DraperyOrderHeadrail;
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

const parseNumber = (value?: string | number) => {
  if (value === null || value === undefined || value === '') return NaN;
  const num = parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(num) ? num : NaN;
};

const toNumber = (value?: string | number) => {
  const parsed = parseNumber(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const computeCalculatedValue = (row: any, draperyOrderHeadrail?: any): number => {
  const quantity = toNumber(row?.quantity);
  const cost = toNumber(row?.cost);
  const markup = toNumber(row?.markup);
  // additional price adjustment
  const additionalPriceAdjustment =
    draperyOrderHeadrail?.headrailAdditionalPriceAdjustmentType || 'none';
  // additional price adjustment value
  const additionalPriceAdjustmentValue =
    Number(draperyOrderHeadrail?.headrailAdditionalPriceAdjustment) || 0;
  // additional price adjustment (discount or markup)
  const additionalPriceAdjustmentCalculate =
    additionalPriceAdjustment === 'discount'
      ? -additionalPriceAdjustmentValue
      : additionalPriceAdjustmentValue;

  const totalPercentage = markup / 100 + additionalPriceAdjustmentCalculate / 100;
  return quantity * cost * (1 + totalPercentage);
};

const formatCurrency = (value: number) => currencyFormatter.format(value);

export const HeadrailPricingTable = ({
  rows,
  shippingRow,
  includeShipping,
  disabled,
  onChange,
  onToggleShipping,
  draperyOrderHeadrail,
}: HeadrailPricingTableProps) => {
  const totals = useMemo(() => {
    const rowsToSum = includeShipping ? [...rows, shippingRow] : rows;
    return rowsToSum.reduce(
      (acc, row) => {
        const baseCost = toNumber(row?.quantity) * toNumber(row?.cost);
        // additional price adjustment
        const additionalPriceAdjustment =
          draperyOrderHeadrail?.headrailAdditionalPriceAdjustmentType || 'none';
        // additional price adjustment value
        const additionalPriceAdjustmentValue =
          Number(draperyOrderHeadrail?.headrailAdditionalPriceAdjustment) || 0;
        // additional price adjustment (discount or markup)
        const additionalPriceAdjustmentCalculate =
          additionalPriceAdjustment === 'discount'
            ? -additionalPriceAdjustmentValue
            : additionalPriceAdjustmentValue;

        const totalPercentage =
          toNumber(row?.markup) / 100 + additionalPriceAdjustmentCalculate / 100;

        const calculated = baseCost * (1 + totalPercentage);
        const customerRaw = parseNumber(row?.customerPrice);
        const customerValue = Number.isNaN(customerRaw) ? calculated : customerRaw;

        acc.cost += baseCost;
        acc.calculated += calculated;
        acc.customer += customerValue;
        return acc;
      },
      { cost: 0, calculated: 0, customer: 0 }
    );
  }, [rows, shippingRow, includeShipping]);

  const inputBase =
    'h-10 rounded-md border border-[#dbe3ee] bg-white px-3 text-[13px] text-[#344054] shadow-[0_1px_2px_rgba(16,24,40,0.04)] focus-visible:border-[#8bc2db] focus-visible:ring-0 disabled:opacity-100 disabled:bg-[#f2f4f7] disabled:text-[#98a2b3]';

  const renderRow = (row: any, index: number, isShipping?: boolean) => {
    const calculatedValue = computeCalculatedValue(row, draperyOrderHeadrail);
    const calculatedDisplay = formatCurrency(calculatedValue);
    const customerPrice =
      row?.customerPrice !== undefined && row?.customerPrice !== null && row?.customerPrice !== ''
        ? row.customerPrice
        : calculatedValue.toFixed(2);
    const rowDisabled = disabled || (isShipping && !includeShipping);

    return (
      <TableRow
        key={isShipping ? 'shipping-row' : row?.id || `headrail-row-${index}`}
        className={cn('border-[#e5e7eb] bg-white', rowDisabled && 'opacity-70')}
      >
        <TableCell className="w-[40%] px-4 py-3">
          <Input
            value={row?.estimateDescription || ''}
            onChange={(e) => onChange(index, 'estimateDescription', e.target.value, isShipping)}
            placeholder={isShipping ? 'Shipping & Handling' : 'Estimate description'}
            disabled={rowDisabled}
            className={inputBase}
          />
        </TableCell>
        <TableCell className="w-[90px] px-4 py-3">
          <Input
            type="number"
            value={row?.quantity || ''}
            onChange={(e) => onChange(index, 'quantity', e.target.value, isShipping)}
            inputMode="decimal"
            min={0}
            step="any"
            placeholder="0"
            disabled={rowDisabled || isShipping}
            className={cn(inputBase, 'text-right')}
          />
        </TableCell>
        <TableCell className="w-[140px] px-4 py-3">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[#667085]">
              $
            </span>
            <Input
              value={row?.cost || ''}
              onChange={(e) => onChange(index, 'cost', e.target.value, isShipping)}
              inputMode="decimal"
              placeholder="0.00"
              disabled={rowDisabled}
              className={cn(inputBase, 'pl-6', 'text-right')}
            />
          </div>
        </TableCell>
        <TableCell className="w-[140px] px-4 py-3">
          <div className="relative">
            <Input
              value={row?.markup || ''}
              onChange={(e) => onChange(index, 'markup', e.target.value, isShipping)}
              inputMode="decimal"
              placeholder="0.00"
              disabled={rowDisabled}
              className={cn(inputBase, 'pr-8', 'text-right')}
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-[#667085]">
              %
            </span>
          </div>
        </TableCell>
        {draperyOrderHeadrail?.headrailAdditionalPriceAdjustmentType &&
        draperyOrderHeadrail?.headrailAdditionalPriceAdjustmentType !== 'none' ? (
          <TableCell className="p-3 text-center text-sm">
            {draperyOrderHeadrail?.headrailAdditionalPriceAdjustmentType === 'discount' && '-'}
            {draperyOrderHeadrail?.headrailAdditionalPriceAdjustment || '0'}%
          </TableCell>
        ) : null}
        <TableCell className="w-[170px] px-4 py-3">
          <div
            className={cn(
              'flex h-9 items-center justify-end rounded-md border border-[#d7dee8] bg-[#f8fafc] px-3 text-[13px] font-semibold text-[#101828]',
              rowDisabled && 'bg-[#f2f4f7] text-[#98a2b3]'
            )}
          >
            {calculatedDisplay}
          </div>
        </TableCell>
        <TableCell className="w-[200px] px-4 py-3">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[#667085]">
              $
            </span>
            <Input
              value={customerPrice}
              onChange={(e) => onChange(index, 'customerPrice', e.target.value, isShipping)}
              inputMode="decimal"
              placeholder="0.00"
              disabled={rowDisabled}
              className={cn(inputBase, 'pl-6', 'text-right')}
            />
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="rounded-lg border border-[#e5e7eb] bg-white">
      <Table className="min-w-[960px]">
        <TableHeader>
          <TableRow className="border-[#e5e7eb] bg-[#eef3f6] text-[#667085]">
            <TableHead className="px-4 text-[13px] font-semibold text-[#334155]">
              Estimate Description
            </TableHead>
            <TableHead className="w-[90px] px-4 text-center text-[13px] font-semibold text-[#334155]">
              Quantity
            </TableHead>
            <TableHead className="w-[140px] px-4 text-center text-[13px] font-semibold text-[#334155]">
              Cost
            </TableHead>
            <TableHead className="w-[140px] px-4 text-center text-[13px] font-semibold text-[#334155]">
              Product Markup
            </TableHead>
            {draperyOrderHeadrail?.headrailAdditionalPriceAdjustmentType &&
              draperyOrderHeadrail?.headrailAdditionalPriceAdjustmentType !== 'none' && (
                <TableHead className="w-[140px] px-4 text-center text-[13px] font-semibold text-[#334155]">
                  Client Price Adjustment
                </TableHead>
              )}
            <TableHead className="w-[170px] px-4 text-center text-[13px] font-semibold text-[#334155]">
              Calculated Price
            </TableHead>
            <TableHead className="w-[200px] px-4 text-center text-[13px] font-semibold text-[#334155]">
              Customer-Facing Price
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {rows.map((row, index) => renderRow(row, index))}

          {includeShipping && renderRow(shippingRow, 0, true)}

          <TableRow className="border-[#e5e7eb] bg-white">
            <TableCell
              colSpan={
                draperyOrderHeadrail?.headrailAdditionalPriceAdjustmentType &&
                draperyOrderHeadrail?.headrailAdditionalPriceAdjustmentType !== 'none'
                  ? 3
                  : 2
              }
              className="px-4 py-3"
            >
              <div className="flex items-center gap-3 text-[13px] font-medium text-[#344054]">
                <Switch
                  checked={includeShipping}
                  onClick={() => !disabled && onToggleShipping(!includeShipping)}
                  disabled={disabled}
                  aria-label="Toggle shipping and handling for headrail"
                />
                <span>Add Shipping & Handling</span>
              </div>
            </TableCell>
            <TableCell colSpan={2} className="px-4 py-3 text-right text-[16px] font-semibold text-[#111827]">
              TOTAL PRICE
            </TableCell>
            <TableCell className="px-4 py-3 text-right text-[16px] font-semibold text-[#111827]">
              {formatCurrency(totals.calculated)}
            </TableCell>
            <TableCell className="px-4 py-3 text-right text-[16px] font-semibold text-[#111827]">
              {formatCurrency(totals.customer)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};
